import { useEffect, useState } from "react";
import { useSelector } from "react-redux";

export default function useLocalStorage<T>(key: string, initialValue: T) {
  // State to store our value
  // Pass initial state function to useState so logic is only executed once
  const [storedValue, setStoredValue] = useState<T>(() => {
    try {
      // Get from local storage by key
      const item = window.localStorage.getItem(key);
      // Parse stored json or if none return initialValue
      return item ? JSON.parse(item) : initialValue;
    } catch {
      // If error also return initialValue
      return initialValue;
    }
  });
  // Return a wrapped version of useState's setter function that ...
  // ... persists the new value to localStorage.
  const setValue = (value: T | ((val: T) => T)) => {
    try {
      // Allow value to be a function so we have same API as useState
      const valueToStore =
        value instanceof Function ? value(storedValue) : value;
      // Save state
      setStoredValue(valueToStore);
      // Save to local storage
      window.localStorage.setItem(key, JSON.stringify(valueToStore));
    } catch {
      // A more advanced implementation would handle the error case
    }
  };
  return [storedValue, setValue] as const;
}

export const useSelectorWithLocalStorage = (
  key: string,
  state: any
): object => {
  const data = useSelector(state);
  const [dataWithLocal, setDataWithLocal] = useLocalStorage(key, data);
  useEffect(() => {
    setDataWithLocal(data);
  }, [data, setDataWithLocal]);

  return dataWithLocal as object;
};
